/**
 * FoodPulseNearbyBusinesses
 * -------------------------
 * A compact "businesses in your city" grid, categorised into rows.
 *
 * Props
 *  - `filterType`   optional single business_type slug (e.g. "bakery")
 *  - `perGroupLimit` how many cards per category row (default 6)
 *
 * Data
 *   Reads city/state/country from `useFoodPulseLocation()` and queries
 *   `/api/food-businesses?city=..&state=..&business_type=..`.
 *   When `filterType` is passed we render a single "Nearby X" row.
 *   Otherwise we fan out to a small set of headline categories:
 *   supermarket · grocery · farm · bakery · restaurant · cafe · butcher.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MapPin, ArrowRight, Phone, Mail, Clock, Globe, MessageCircle, ThumbsUp, ShieldCheck, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { http } from "@/lib/api";
import { useFoodPulseLocation } from "@/components/FoodPulseLocationBar";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";
const PURPLE = "#7C3AED";

const HEADLINE_CATEGORIES = [
  { key: "supermarket", label: "Supermarkets" },
  { key: "grocery", label: "Grocery Stores" },
  { key: "farm", label: "Farms & Farm Stands" },
  { key: "bakery", label: "Bakeries" },
  { key: "restaurant", label: "Restaurants" },
  { key: "cafe", label: "Cafés" },
  { key: "butcher", label: "Butchers" },
  { key: "fishmonger", label: "Fishmongers" },
];

/**
 * Formats a business hours object (day → {open, close, closed}) into
 * a short "Mon-Fri 08:00–20:00 · Sat 09:00–15:00 · Sun closed" string.
 * Best-effort: gracefully skips missing days.
 */
function formatHours(hours) {
  if (!hours || typeof hours !== "object") return null;
  const dow = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const parts = dow.map((d) => {
    const h = hours[d]; if (!h) return null;
    if (h.closed) return `${d.slice(0,3)} closed`;
    if (h.open && h.close) return `${d.slice(0,3)} ${h.open}–${h.close}`;
    return null;
  }).filter(Boolean);
  return parts.length ? parts.slice(0, 4).join(" · ") + (parts.length > 4 ? "…" : "") : null;
}

function BusinessCard({ b }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [likes, setLikes] = useState(b.likes_count || 0);
  const [liked, setLiked] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(b.photo_url || null);

  // Lazy photo enrichment — hits our own endpoint which caches Wikimedia
  // Commons results per slug so this is a no-op after the first visit.
  useEffect(() => {
    if (photoUrl) return;
    let cancelled = false;
    http.get(`/food-businesses/${b.slug}/photo`)
      .then((r) => { if (!cancelled && r.data.photo_url) setPhotoUrl(r.data.photo_url); })
      .catch(() => { /* silent — falls back to placeholder */ });
    return () => { cancelled = true; };
  }, [b.slug, photoUrl]);

  const loadDetail = async () => {
    if (detail || detailLoading) return;
    setDetailLoading(true);
    try {
      const r = await http.get(`/food-businesses/${b.slug}`);
      setDetail(r.data);
    } catch { /* noop */ }
    setDetailLoading(false);
  };
  const toggle = () => {
    setOpen((o) => { if (!o) loadDetail(); return !o; });
  };
  const vouch = async () => {
    if (likeBusy || liked) return;
    setLikeBusy(true);
    try {
      await http.post(`/food-businesses/${b.slug}/vouch`, {});
      setLikes((n) => n + 1); setLiked(true);
    } catch (e) { /* if not signed in, just no-op silently */ }
    setLikeBusy(false);
  };

  const address = detail?.address_line1 ? [detail.address_line1, detail.address_line2, detail.city, detail.state, detail.zip].filter(Boolean).join(", ") : null;
  const hoursStr = formatHours(detail?.fp_business_hours || detail?.hours);
  const email = detail?.email || detail?.fp_business_info?.email;
  const cat = b.sub_type || b.business_type;

  return (
    <div className="rounded-xl border bg-white transition hover:shadow-md overflow-hidden" style={{ borderColor: "#E9D8B4" }} data-testid={`nearby-biz-${b.slug}`}>
      {photoUrl && (
        <Link to={`/food-pulse/businesses/${b.slug}`} className="block">
          <img
            src={photoUrl}
            alt={b.name}
            loading="lazy"
            className="w-full h-24 object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; setPhotoUrl(null); }}
            data-testid={`nearby-photo-${b.slug}`}
          />
        </Link>
      )}
      <div className="p-3">
        <div className="flex items-start gap-2 justify-between">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-mono tracking-widest" style={{ color: PURPLE }}>{cat}</div>
            <Link to={`/food-pulse/businesses/${b.slug}`} className="text-sm font-bold hover:underline block" style={{ color: GREEN }}>{b.name}</Link>
            <div className="text-xs text-gray-600 truncate flex items-center gap-1"><MapPin size={10} /> {[b.city, b.state].filter(Boolean).join(", ") || "—"}</div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {(b.verified || b.claim_status === "claimed") && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-0.5" style={{ background: "#BBF7D0", color: GREEN }}><ShieldCheck size={10} />Verified</span>}
          </div>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-gray-700">
          {b.phone && <a href={`tel:${b.phone}`} className="inline-flex items-center gap-0.5 hover:underline"><Phone size={10} />{b.phone}</a>}
          {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:underline truncate max-w-[100px]" style={{ color: PURPLE }}><Globe size={10} />Website</a>}
        </div>

        <button onClick={toggle} className="mt-2 text-xs font-semibold flex items-center gap-1" style={{ color: GREEN }} data-testid={`nearby-toggle-${b.slug}`}>
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {open ? "Hide details" : "Details"}
        </button>

        {open && (
          <div className="mt-2 border-t pt-2 text-xs" style={{ borderColor: "#F5E6C4" }}>
            {detailLoading && <div className="text-center py-2"><Loader2 className="animate-spin inline" size={12} /></div>}
            {detail && (
              <div className="space-y-1">
                {address && <div className="flex items-start gap-1"><MapPin size={10} className="mt-0.5" />{address}</div>}
                {email && <a href={`mailto:${email}`} className="flex items-center gap-1 hover:underline"><Mail size={10} />{email}</a>}
                {hoursStr && <div className="flex items-start gap-1"><Clock size={10} className="mt-0.5" />{hoursStr}</div>}
                {!address && !email && !hoursStr && <div className="italic text-gray-500">Contact info coming soon — help us fill it in.</div>}
              </div>
            )}
          </div>
        )}

        <div className="mt-2 flex items-center gap-2">
          <button onClick={vouch} disabled={liked} className="text-xs font-semibold inline-flex items-center gap-1 rounded-full px-2 py-1 border disabled:opacity-60" style={{ borderColor: liked ? "#BBF7D0" : GREEN, color: liked ? GREEN : GREEN, background: liked ? "#F0FDF4" : "white" }} data-testid={`nearby-like-${b.slug}`}>
            <ThumbsUp size={10} /> {likes || "Like"}
          </button>
          <Link to={`/food-pulse/businesses/${b.slug}#comments`} className="text-xs font-semibold inline-flex items-center gap-1 rounded-full px-2 py-1 border" style={{ borderColor: GREEN, color: GREEN }} data-testid={`nearby-comment-${b.slug}`}>
            <MessageCircle size={10} /> Comment
          </Link>
          {b.claim_status === "unclaimed" && (
            <Link to={`/food-pulse/claim?slug=${b.slug}`} className="ml-auto text-xs font-bold inline-flex items-center gap-1 rounded-full px-2 py-1" style={{ background: PURPLE, color: "white" }} data-testid={`nearby-claim-${b.slug}`}>
              Claim
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ label, items, seeAllHref, loading, categoryKey, city }) {
  return (
    <section className="mb-6" data-testid={`nearby-row-${categoryKey}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-bold" style={{ color: GREEN }}>{label}</h3>
        {seeAllHref && <Link to={seeAllHref} className="text-xs font-semibold flex items-center gap-1" style={{ color: PURPLE }}>See all <ArrowRight size={12} /></Link>}
      </div>
      {loading ? (
        <div className="p-4 text-center"><Loader2 className="animate-spin mx-auto" size={16} /></div>
      ) : items.length === 0 ? (
        <div className="text-xs border rounded-lg p-4 flex items-center justify-between gap-2 flex-wrap" style={{ borderColor: "#E9D8B4", background: "#FEF9E7" }}>
          <span className="italic text-gray-600">No {label.toLowerCase()} listed in {city || "your city"} yet.</span>
          <Link to={`/food-pulse/business/signup?category=${categoryKey}${city ? "&city=" + encodeURIComponent(city) : ""}`} className="rounded-full px-3 py-1 font-bold" style={{ background: PURPLE, color: "white" }} data-testid={`suggest-btn-${categoryKey}`}>Suggest a business →</Link>
        </div>
      ) : (
        <div className="grid gap-2 md:grid-cols-3">{items.map((b) => <BusinessCard key={b.id || b.slug} b={b} />)}</div>
      )}
    </section>
  );
}

export default function FoodPulseNearbyBusinesses({ filterType, filterLabel, perGroupLimit = 6 }) {
  const loc = useFoodPulseLocation();
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(false);

  const cats = useMemo(() => {
    if (filterType) {
      return [{ key: filterType, label: `Nearby ${filterLabel || (filterType.charAt(0).toUpperCase() + filterType.slice(1))}` }];
    }
    return HEADLINE_CATEGORIES;
  }, [filterType, filterLabel]);

  useEffect(() => {
    if (!loc?.city) { setGroups({}); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const next = {};
      await Promise.all(cats.map(async (c) => {
        try {
          const r = await http.get("/food-businesses", { params: { city: loc.city, state: loc.state || undefined, business_type: c.key, limit: perGroupLimit } });
          next[c.key] = r.data.items || r.data || [];
        } catch { next[c.key] = []; }
      }));
      if (!cancelled) { setGroups(next); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [loc?.city, loc?.state, cats, perGroupLimit]);

  if (!loc?.city) {
    return (
      <div className="rounded-2xl border p-6 text-center bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="nearby-no-location">
        <MapPin size={32} className="mx-auto mb-2" style={{ color: PURPLE }} />
        <div className="text-sm text-gray-700">Set your city above to see nearby food businesses.</div>
      </div>
    );
  }

  return (
    <div data-testid="fp-nearby-businesses">
      {/* Suggest-a-business banner — crowdsourcing CTA visible on every render */}
      <div className="rounded-2xl mb-4 p-4 flex items-center justify-between gap-3 flex-wrap" style={{ background: "linear-gradient(90deg,#FEF3C7,#E9D8FB)" }} data-testid="suggest-business-banner">
        <div className="flex items-start gap-2">
          <Sparkles size={18} style={{ color: PURPLE }} />
          <div>
            <div className="text-sm font-bold" style={{ color: GREEN }}>Missing a business we should list?</div>
            <div className="text-xs text-gray-700">FoodPulse is crowdsourced — help your neighbours discover the good stores near <b>{loc.city}</b>.</div>
          </div>
        </div>
        <Link to={`/food-pulse/business/signup?city=${encodeURIComponent(loc.city)}&state=${encodeURIComponent(loc.state || "")}`} className="rounded-full px-4 py-2 text-xs font-bold" style={{ background: PURPLE, color: "white" }} data-testid="suggest-business-cta">
          Suggest a business →
        </Link>
      </div>

      <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Food businesses in <b style={{ color: GREEN }}>{loc.city}</b></div>
      {cats.map((c) => (
        <CategoryRow
          key={c.key}
          label={c.label}
          items={groups[c.key] || []}
          loading={loading}
          categoryKey={c.key}
          city={loc.city}
          seeAllHref={`/food-pulse/city?city=${encodeURIComponent(loc.city)}&state=${encodeURIComponent(loc.state || "")}&type=${c.key}`}
        />
      ))}
    </div>
  );
}
